import { WebViewerInstance } from '@pdftron/webviewer';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { type PrintPageScale } from '@frontend/api';

import { getPrintPageScales } from 'modules/common/viewer/data/reducer';
import { rerenderPageScales } from 'modules/common/viewer/pdfViewer/data/actions';
import { MEASUREMENT_TOOL_NAMES } from 'modules/common/viewer/pdfViewer/data/constants';
import {
  getCurrentPageNumber,
  getManualScaleMeasurementToolsRequest,
  getRerenderAllMeasurementAnnotationsRequest,
} from 'modules/common/viewer/pdfViewer/data/reducer';

import { isMeasurementAnnotation } from './useAnnotationEventListeners';

type UseScaleMeasurementsProps = {
  instance: WebViewerInstance | null;
};

export const useScaleMeasurements = ({
  instance,
}: UseScaleMeasurementsProps) => {
  const currentPageNumber = useSelector(getCurrentPageNumber);
  const pageScales: Record<number, PrintPageScale> =
    useSelector(getPrintPageScales);
  const rerenderAllMeasurementAnnotationsRequest = useSelector(
    getRerenderAllMeasurementAnnotationsRequest,
  );
  const manuallyScaleMeasurementToolsRequest = useSelector(
    getManualScaleMeasurementToolsRequest,
  );

  const dispatch = useDispatch();

  // Single effect to handle both measurement tools and annotations scaling
  useEffect(() => {
    if (!instance || !pageScales[currentPageNumber]) return;

    const currentPageScale = pageScales[currentPageNumber];

    // First, update measurement tools for current page
    if (manuallyScaleMeasurementToolsRequest) {
      const measurementTools = MEASUREMENT_TOOL_NAMES(instance.Core.Tools);
      measurementTools.forEach((toolName) => {
        const tool = instance.Core.documentViewer.getTool(toolName);
        tool.setStyles({
          Scale: [
            [
              currentPageScale.scaleFromDimension,
              currentPageScale.scaleFromUnit,
            ],
            [currentPageScale.scaleToDimension, currentPageScale.scaleToUnit],
          ],
        });
      });
      dispatch(rerenderPageScales.toolsSuccess());
    }

    // Then, update annotations only for current page
    if (rerenderAllMeasurementAnnotationsRequest) {
      const measurementAnnotationsOnCurrentPage =
        instance.Core.annotationManager
          .getAnnotationsList()
          .filter(isMeasurementAnnotation)
          .filter(
            (annotation) =>
              annotation.getPageNumber().toString() ===
              currentPageNumber.toString(),
          );

      measurementAnnotationsOnCurrentPage?.forEach((measurementAnnotation) => {
        measurementAnnotation.Scale = [
          [currentPageScale.scaleFromDimension, currentPageScale.scaleFromUnit],
          [currentPageScale.scaleToDimension, currentPageScale.scaleToUnit],
        ];
        instance.Core.annotationManager.redrawAnnotation(measurementAnnotation);
      });

      dispatch(rerenderPageScales.annotationsSuccess());
    }
  }, [
    dispatch,
    instance,
    currentPageNumber,
    pageScales,
    manuallyScaleMeasurementToolsRequest,
    rerenderAllMeasurementAnnotationsRequest,
  ]);
};
